import { useState } from 'react';
import {
  ArrowLeftIcon,
  CheckCircleIcon,
  ArrowPathIcon,
  PencilSquareIcon,
  PaperAirplaneIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';
import { useSettings } from '../../contexts/SettingsContext';
import {
  TEMPLATE_TYPES,
  substitutePreviewValues,
  type EmailTemplateType,
} from '../../lib/emailTemplates';
import { sendSampleEmail } from '../../lib/email';
import EmailTemplateEditor from './EmailTemplateEditor';
import EmailTemplatePreview from './EmailTemplatePreview';

export default function CommunicationSettingsForm() {
  const {
    state,
    updateCommunicationSetting,
    addCommunicationSetting,
  } = useSettings();

  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editSubject, setEditSubject] = useState('');
  const [editHtml, setEditHtml] = useState('');
  const [saving, setSaving] = useState(false);
  const [showSendSample, setShowSendSample] = useState(false);
  const [sampleName, setSampleName] = useState('');
  const [sampleEmail, setSampleEmail] = useState('');
  const [sendingSample, setSendingSample] = useState(false);

  const getExistingTemplate = (templateType: EmailTemplateType) =>
    state.communicationSettings.find(
      (s) => s.type === 'email' && s.name === templateType.dbName
    );

  const getResolvedTemplate = (templateType: EmailTemplateType) => {
    const existing = getExistingTemplate(templateType);
    const isBranded = existing?.template?.includes('background-color: #1A472A') || existing?.template?.includes('background-color:#1A472A');
    const hasAppDownload = existing?.template?.includes('appStoreUrl');
    const useExisting = !!existing && isBranded && hasAppDownload;
    return {
      subject: existing?.subject || templateType.defaultSubject,
      html: useExisting ? existing!.template : templateType.defaultHtml,
      isCustomized: useExisting,
      isActive: existing?.is_active ?? true,
    };
  };

  const handleEdit = (index: number) => {
    const t = TEMPLATE_TYPES[index];
    const resolved = getResolvedTemplate(t);
    setEditSubject(resolved.subject);
    setEditHtml(resolved.html);
    setEditingIndex(index);
  };

  const handleCancel = () => {
    setEditingIndex(null);
    setEditSubject('');
    setEditHtml('');
  };

  const handleReset = () => {
    if (editingIndex === null) return;
    const t = TEMPLATE_TYPES[editingIndex];
    setEditSubject(t.defaultSubject);
    setEditHtml(t.defaultHtml);
    toast.success('Reset to default template');
  };

  const handleSave = async () => {
    if (editingIndex === null) return;
    const templateType = TEMPLATE_TYPES[editingIndex];
    const existing = getExistingTemplate(templateType);

    setSaving(true);
    try {
      const placeholdersObj = templateType.placeholders.reduce(
        (acc, p) => {
          acc[p.key] = p.label;
          return acc;
        },
        {} as Record<string, string>
      );

      if (existing) {
        await updateCommunicationSetting({
          ...existing,
          subject: editSubject,
          template: editHtml,
          placeholders: placeholdersObj,
          is_active: true,
        });
      } else {
        await addCommunicationSetting({
          type: 'email',
          name: templateType.dbName,
          subject: editSubject,
          template: editHtml,
          placeholders: placeholdersObj,
          is_active: true,
        });
      }
      setEditingIndex(null);
      setEditSubject('');
      setEditHtml('');
    } catch {
      toast.error('Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  const handleSendSample = async () => {
    if (editingIndex === null) return;
    if (!sampleEmail.trim()) {
      toast.error('Please enter an email address');
      return;
    }
    if (!sampleName.trim()) {
      toast.error('Please enter a name');
      return;
    }

    const templateType = TEMPLATE_TYPES[editingIndex];
    setSendingSample(true);

    try {
      const placeholders: Record<string, string> = {};
      for (const p of templateType.placeholders) {
        if (p.key === 'firstName') {
          placeholders[p.key] = sampleName.split(' ')[0] || sampleName;
        } else if (p.key === 'lastName') {
          placeholders[p.key] = sampleName.split(' ').slice(1).join(' ') || '';
        } else {
          placeholders[p.key] = p.example;
        }
      }

      const result = await sendSampleEmail(templateType.id, sampleEmail.trim(), placeholders);

      if (result.success) {
        toast.success(`Sample email sent to ${sampleEmail.trim()}`);
        setShowSendSample(false);
      } else {
        toast.error(result.message || 'Failed to send sample email');
      }
    } catch {
      toast.error('Failed to send sample email');
    } finally {
      setSendingSample(false);
    }
  };

  if (editingIndex !== null) {
    const templateType = TEMPLATE_TYPES[editingIndex];
    const previewHtml = substitutePreviewValues(editHtml, templateType.placeholders);

    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <button
              onClick={handleCancel}
              className="p-2 rounded-lg hover:bg-gray-100 transition-colors text-gray-500"
            >
              <ArrowLeftIcon className="w-5 h-5" />
            </button>
            <div>
              <h2 className="text-xl font-semibold text-golf-gray">
                {templateType.name}
              </h2>
              <p className="text-sm text-gray-500">{templateType.description}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowSendSample(!showSendSample)}
              className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                showSendSample
                  ? 'text-golf-orange bg-golf-orange/10 border border-golf-orange/30'
                  : 'text-gray-600 bg-white border border-gray-300 hover:bg-gray-50'
              }`}
            >
              <PaperAirplaneIcon className="w-4 h-4" />
              Send Sample
            </button>
            <button
              onClick={handleReset}
              className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <ArrowPathIcon className="w-4 h-4" />
              Reset
            </button>
            <button
              onClick={handleCancel}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-4 py-2 text-sm font-medium text-white bg-golf-green rounded-lg hover:bg-golf-green/90 transition-colors disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Save Template'}
            </button>
          </div>
        </div>

        {showSendSample && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h3 className="text-sm font-semibold text-gray-900">Send Sample Email</h3>
                <p className="text-xs text-gray-500 mt-0.5">
                  Send this template with example data to a test recipient
                </p>
              </div>
              <button
                onClick={() => setShowSendSample(false)}
                className="p-1 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
              >
                <XMarkIcon className="w-4 h-4" />
              </button>
            </div>
            <div className="flex items-end gap-3 flex-wrap">
              <div className="flex-1 min-w-[160px]">
                <label className="block text-xs font-medium text-gray-600 mb-1">Recipient Name</label>
                <input
                  type="text"
                  value={sampleName}
                  onChange={(e) => setSampleName(e.target.value)}
                  placeholder="John Doe"
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-golf-green/20 focus:border-golf-green transition-colors"
                />
              </div>
              <div className="flex-1 min-w-[200px]">
                <label className="block text-xs font-medium text-gray-600 mb-1">Email Address</label>
                <input
                  type="email"
                  value={sampleEmail}
                  onChange={(e) => setSampleEmail(e.target.value)}
                  placeholder="john@example.com"
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-golf-green/20 focus:border-golf-green transition-colors"
                />
              </div>
              <button
                onClick={handleSendSample}
                disabled={sendingSample || !sampleEmail.trim() || !sampleName.trim()}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-golf-orange rounded-lg hover:bg-golf-orange/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
              >
                <PaperAirplaneIcon className="w-4 h-4" />
                {sendingSample ? 'Sending...' : 'Send'}
              </button>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4" style={{ minHeight: 700 }}>
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 flex flex-col overflow-hidden">
            <EmailTemplateEditor
              subject={editSubject}
              html={editHtml}
              placeholders={templateType.placeholders}
              onSubjectChange={setEditSubject}
              onHtmlChange={setEditHtml}
            />
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 flex flex-col overflow-hidden">
            <EmailTemplatePreview html={previewHtml} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-golf-gray">Email Templates</h2>
        <p className="text-sm text-gray-500 mt-1">
          Customize the email templates sent to players and participants.
        </p>
      </div>

      {state.loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-golf-green border-t-transparent" />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
          {TEMPLATE_TYPES.map((templateType, index) => {
            const resolved = getResolvedTemplate(templateType);
            const previewHtml = substitutePreviewValues(
              resolved.html,
              templateType.placeholders
            );

            return (
              <div
                key={templateType.id}
                className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden group hover:shadow-md hover:border-gray-300 transition-all"
              >
                <div className="relative">
                  <EmailTemplatePreview html={previewHtml} compact />
                  <div className="absolute inset-0 bg-gradient-to-t from-white via-transparent to-transparent" />
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/5">
                    <button
                      onClick={() => handleEdit(index)}
                      className="flex items-center gap-2 px-4 py-2.5 bg-white text-golf-green font-medium rounded-lg shadow-lg hover:bg-gray-50 transition-colors text-sm border border-gray-200"
                    >
                      <PencilSquareIcon className="w-4 h-4" />
                      Edit Template
                    </button>
                  </div>
                </div>
                <div className="p-4 border-t border-gray-100">
                  <div className="flex items-center justify-between mb-1">
                    <h3 className="font-semibold text-gray-900 text-sm">
                      {templateType.name}
                    </h3>
                    <span
                      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                        resolved.isCustomized
                          ? 'bg-golf-green/10 text-golf-green'
                          : 'bg-gray-100 text-gray-500'
                      }`}
                    >
                      {resolved.isCustomized && (
                        <CheckCircleIcon className="w-3 h-3" />
                      )}
                      {resolved.isCustomized ? 'Customized' : 'Default'}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 mb-3">
                    {templateType.description}
                  </p>
                  <div className="flex items-center gap-2 flex-wrap">
                    {templateType.placeholders.slice(0, 3).map((p) => (
                      <span
                        key={p.key}
                        className="inline-block px-2 py-0.5 rounded text-xs bg-gray-100 text-gray-600 font-mono"
                      >
                        {`{{${p.key}}}`}
                      </span>
                    ))}
                    {templateType.placeholders.length > 3 && (
                      <span className="text-xs text-gray-400">
                        +{templateType.placeholders.length - 3} more
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
